import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth.js'
import { setTokenGetter } from '../services/api.js'
import { useSubscriptionStore } from '../stores/subscriptionStore.js'
import { getMyEntitlement } from '../services/subscriptionService.js'
import { getVehicles } from '../services/vehicleService.js'

/** Retry a function up to `attempts` times with exponential backoff */
const withRetry = async (fn, attempts = 3) => {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn()
    } catch (err) {
      if (i === attempts - 1) throw err
      await new Promise((r) => setTimeout(r, 1000 * (i + 1)))
    }
  }
}

const AuthInitializer = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth()
  const loadFromBackend = useSubscriptionStore((s) => s.loadFromBackend)
  const setVehicleCount = useSubscriptionStore((s) => s.setVehicleCount)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) return

    const init = async () => {
      // 1. Set token getter FIRST — so all subsequent API calls have the token
      setTokenGetter(getAccessTokenSilently)

      // 2. Wait for at least one token to be available
      try {
        await getAccessTokenSilently()
      } catch (err) {
        console.warn('Could not obtain initial token:', err)
      }

      // 3. Load entitlement (with retry) and vehicles in parallel
      const results = await Promise.allSettled([
        withRetry(() => getMyEntitlement().then((ent) => loadFromBackend(ent))),
        getVehicles().then((vehicles) => setVehicleCount(Array.isArray(vehicles) ? vehicles.length : 0)),
      ])

      // Log any failures but don't block rendering
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const label = i === 0 ? 'entitlements' : 'vehicles'
          console.warn(`Failed to load ${label}:`, r.reason?.response?.status || r.reason?.message)
        }
      })

      setReady(true)
    }

    init()
  }, [isAuthenticated, getAccessTokenSilently, loadFromBackend, setVehicleCount])

  // Show brief loading state while initializing, but with a short timeout fallback
  useEffect(() => {
    if (!ready) {
      const timeout = setTimeout(() => setReady(true), 5000)
      return () => clearTimeout(timeout)
    }
  }, [ready])

  if (!ready) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading your data...</p>
        </div>
      </div>
    )
  }

  return children
}

export default AuthInitializer
